import { createRouter, toRouteMatcher, type RouteMatcher } from 'radix3';
import { DEFAULT_LOCALE, type LocaleCode } from '~/constants/i18n';
import { isStaffPath, localeFromRouteName, normalizeLocale, stripLocalePrefix } from '~/utils/i18n/locale';

/** useState key carrying the cookie locale the server rendered with, so the client hydrates in it. */
export const SSR_COOKIE_LOCALE_STATE_KEY = 'i18n:ssr-cookie-locale';

// Rule keys whose presence means one rendered HTML is stored and served to many visitors.
// `payload` is how Nuxt's app-side route-rule matcher (`getRouteRules` from '#app') reports any
// swr/isr/cache rule — it collapses those three into `payload: true`.
const SHARED_CACHE_RULE_KEYS = ['swr', 'isr', 'cache', 'prerender', 'payload'] as const;

const NUXT_ERROR_PATH = '/__nuxt_error';

type RouteRules = Record<string, Record<string, unknown>>;

// Nitro matches routeRules with radix3 (nitropack runtime/internal/route-rules.mjs:
// `toRouteMatcher(createRouter({ routes })).matchAll(path)`), so the same matcher is used here
// to agree with it exactly — including its quirks (e.g. a partial-segment glob like `/foo/v*` matches nothing).
const matchers = new WeakMap<RouteRules, RouteMatcher>();
const matcherFor = (routeRules: RouteRules): RouteMatcher => {
  let matcher = matchers.get(routeRules);
  if (!matcher) {
    matcher = toRouteMatcher(createRouter({ routes: routeRules }));
    matchers.set(routeRules, matcher);
  }
  return matcher;
};

export const isSharedCacheRule = (rule: Record<string, unknown>): boolean =>
  SHARED_CACHE_RULE_KEYS.some((key) => {
    const value = rule[key];
    return value !== undefined && value !== false && value !== null;
  });

/**
 * Whether the server may render `path` in a per-visitor (cookie) locale. False for staff tools
 * (always English), locale-prefixed URLs (the URL decides), and any path matched by a shared-cache
 * route rule (swr/isr/cache/prerender) — a cached per-visitor render would be served to everyone.
 * Pass the PRODUCTION rule set (constants/routeRules.ts) regardless of environment, plus — belt
 * and braces for rules added elsewhere later — the rules actually live for `path` in this build
 * (`liveRules`, already merged for the path).
 * Paths with `ssr: false` return true; harmless, as the server never renders them.
 */
export const isCacheSafeForCookieLocale = (
  path: string,
  routeRules: RouteRules,
  liveRules: Record<string, unknown> = {},
): boolean => {
  const pathname = path.split(/[?#]/)[0] || '/';
  if (isStaffPath(pathname)) return false;
  if (stripLocalePrefix(pathname) !== pathname) return false;
  if (isSharedCacheRule(liveRules)) return false;
  return !matcherFor(routeRules).matchAll(pathname).some(isSharedCacheRule);
};

/**
 * The page path being rendered. Nuxt renders error pages through an internal
 * `/__nuxt_error?url=<original>` request (cookies forwarded) and sets `ssrContext.url` to the
 * original URL — so the request path alone would be `/__nuxt_error` and slip past every guard.
 * Prefers `ssrContextUrl`; falls back to the error request's `url` query, then the request path.
 * Returns the pathname only (no query/hash), or null when an error render's page is unknown.
 */
export const resolveRenderedPath = (ssrContextUrl: string | null | undefined, eventPath: string): string | null => {
  const [eventPathname, eventQuery = ''] = eventPath.split('?');
  const isErrorRender = eventPathname === NUXT_ERROR_PATH;
  const raw = ssrContextUrl || (isErrorRender ? new URLSearchParams(eventQuery).get('url') : eventPath);
  if (!raw) return null;
  const pathname = new URL(raw, 'http://localhost').pathname;
  return pathname === NUXT_ERROR_PATH ? null : pathname;
};

export type SsrCookieLocaleInput = {
  path: string;
  /** Name of the route `path` resolves to; localized routes carry a `___<locale>` suffix. */
  routeName: unknown;
  cookie: string | null | undefined;
  enabled: readonly LocaleCode[];
  routeRules: RouteRules;
  /** Rules live for `path` in this build (e.g. Nuxt's `getRouteRules({ path })`). */
  liveRules?: Record<string, unknown>;
};

/**
 * The locale the server should render an opted-out, uncached route in: the `i18n_locale` cookie
 * (only — never Accept-Language or a profile lookup) when enabled and non-default. Null means
 * "leave the default" (not eligible, no/unknown/disabled cookie, or English).
 */
export const resolveSsrCookieLocale = ({
  path,
  routeName,
  cookie,
  enabled,
  routeRules,
  liveRules,
}: SsrCookieLocaleInput): LocaleCode | null => {
  if (localeFromRouteName(routeName)) return null;
  if (!isCacheSafeForCookieLocale(path, routeRules, liveRules)) return null;
  const code = normalizeLocale(cookie);
  if (!code || code === DEFAULT_LOCALE || !enabled.includes(code)) return null;
  return code;
};
